import "./subtree.scss";

import { ViewerEventMap } from "openseadragon";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import {
  Annotation,
  AnnotationType,
  AreaCountAnnotation,
  MeasurementAnnotation,
  PenDrawingAnnotation,
  RegionOfInterestAnnotation,
  ScaleInfo,
  TextAnnotation,
  ViewerMode,
} from "custom-models/ViewerTypes";

import {
  addAnnotation,
  startNewPenDrawing,
} from "shared/actions/slideDetailActions";
import {
  LesionLine,
  lesionLineTypeByName,
  ROIPosition,
} from "shared/reducers/aiReducer";
import { notFound } from "shared/utils/annotations";

import { selectSlideClassification } from "selectors/classificationSelector";
import { useAppSelector } from "store/hooks";

import AIRegion from "./AIRegion";
import AreaCount from "./AreaCount";
import AreaCountTool from "./AreaCountTool";
import Measurement from "./Measurement";
import MeasurementTool from "./MeasurementTool";
import PenDrawing from "./PenDrawing";
import PenDrawingTool from "./PenDrawingTool";
import RegionOfInterest from "./RegionOfInterest";
import RegionOfInterestTool from "./RegionOfInterestTool";
import Text from "./Text";
import TextTool from "./TextTool";
import ViewerFacade from "../ViewerFacade";

const SvgRoot = (props: { viewerFacade: ViewerFacade }) => {
  const auth = useAppSelector(({ auth }) => auth);
  const slideDetail = useAppSelector(({ slideDetail }) => slideDetail);
  const features = useAppSelector(({ sys }) => sys.features);
  const maskLesionLineType = useAppSelector(({ ai }) => ai.maskLesionLineType);

  const ai = useAppSelector((state) =>
    selectSlideClassification(state, slideDetail.id)
  );

  const [scaleInfo, setScaleInfo] = useState<ScaleInfo | undefined>(
    props.viewerFacade.extractScaleInfo()
  );

  useEffect(() => {
    const handlerIds = [
      "zoom",
      "rotate",
      "reset-size",
      "after-resize",
      "constrain",
    ].map((handler) =>
      props.viewerFacade.addHandler(handler as keyof ViewerEventMap, () => {
        setScaleInfo(props.viewerFacade.extractScaleInfo());
      })
    );

    return () => {
      handlerIds.forEach((handlerId) =>
        props.viewerFacade.removeHandler(handlerId)
      );
    };
  }, []);

  const dispatch = useDispatch();

  const viewerMode = slideDetail.viewerMode;
  const annotations: Annotation[] = slideDetail.annotations || [];
  const selectedAnnotation = slideDetail.selectedAnnotation;
  const lesion_lines = ai?.lesion_lines;
  const aiOverlay = features.aiOverlay || false;

  const onAnnotationComplete = (annotation: Annotation) => {
    if (
      notFound(slideDetail.pendingAnnotationsBuffer, annotation) &&
      slideDetail.id
    ) {
      dispatch(addAnnotation(annotation, slideDetail.id));
    }
  };

  const roiToDziPositions = (
    wsi_lvl_roi_positions?: ROIPosition[]
  ): ROIPosition[] => {
    // Jason's original comment below:
    /*
    AI uses a WSI coordinate system with x=0, y=0 relative to the WSI top left corner.
    In WSI coordinate system, the DZI top left corner is at (dziOffsetX, dziOffsetY).
    Front end's coordinate system sets x=0, y=0 at the DZI top left corner.
    So to translate WSI to DZI coordinates, we subtract dziOffsetX, dziOffsetY,
    analogous to subtracting 273 to convert Kelvin to Celsius.
    The WSI width/height, DZI width/height, and DZI offset x/y all come from the
    tile server XML response and are stored in the ViewerFacade to create ScaleInfo objects.
    This is currently the only place we need to perform this offset translation;
    the front end doesn't encounter WSI coordinates in any other context.
    */
    // Kept for posterity's sake -- Evan (12/20/22)

    return (wsi_lvl_roi_positions || []).map((wsi: ROIPosition) => ({
      ...wsi,
      center_point: [
        wsi.center_point[0] - (scaleInfo?.dziOffsetX || 0),
        wsi.center_point[1] - (scaleInfo?.dziOffsetY || 0),
      ],
    }));
  };

  const lesionLine: LesionLine | undefined = lesion_lines?.find(
    (line) =>
      lesionLineTypeByName(line?.diagnosis_name || "") === maskLesionLineType
  );

  if (
    !scaleInfo?.width ||
    !scaleInfo?.height ||
    !scaleInfo?.scaleX ||
    !scaleInfo?.scaleY
  ) {
    return <></>;
  }
  return (
    <div
      style={{
        transformOrigin: "top left",
        transform: `scale(${scaleInfo.scaleX},${scaleInfo.scaleY})`,
      }}
    >
      {auth.user && (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          version="1.1"
          preserveAspectRatio="none"
          width={scaleInfo.width}
          height={scaleInfo.height}
          className="osd-overlay-svg"
        >
          {!(
            slideDetail.annotationsHidden &&
            slideDetail.viewerMode === ViewerMode.PAN_ZOOM
          ) &&
            (annotations || [])
              .concat(slideDetail.pendingAnnotationsBuffer)
              .map((annotation) => {
                switch (annotation.type) {
                  case AnnotationType.MEASUREMENT:
                    return (
                      scaleInfo && ( // "TS 2322: Object is possibly null"
                        <Measurement
                          key={`measurement_annotation_id_${
                            annotation.created_at
                          }_${annotation.id || 0}`}
                          annotation={annotation as MeasurementAnnotation}
                          scaleInfo={scaleInfo}
                          viewerFacade={props.viewerFacade}
                          isSelected={annotation === selectedAnnotation}
                        />
                      )
                    );
                  case AnnotationType.PEN_DRAWING:
                    return (
                      <PenDrawing
                        key={`pen_drawing_annotation_id_${
                          annotation.created_at
                        }_${annotation.id || 0}`}
                        annotation={annotation as PenDrawingAnnotation}
                        scaleInfo={scaleInfo}
                        viewerFacade={props.viewerFacade}
                        isSelected={annotation === selectedAnnotation}
                      />
                    );
                  case AnnotationType.TEXT:
                    return (
                      <Text
                        key={`text_annotation_id_${annotation.created_at}_${
                          annotation.id || 0
                        }`}
                        annotation={annotation as TextAnnotation}
                        scaleInfo={scaleInfo}
                        viewerFacade={props.viewerFacade}
                        isSelected={annotation === selectedAnnotation}
                      />
                    );
                  case AnnotationType.AREA_COUNT:
                    return (
                      <AreaCount
                        key={`area_count_annotation_id_${
                          annotation.created_at
                        }_${annotation.id || 0}`}
                        annotation={annotation as AreaCountAnnotation}
                        scaleInfo={scaleInfo}
                        viewerFacade={props.viewerFacade}
                        isSelected={annotation === selectedAnnotation}
                      />
                    );
                  case AnnotationType.REGION_OF_INTEREST:
                    const roiAnnotation =
                      annotation as RegionOfInterestAnnotation;
                    return (
                      features.prospectiveAI && (
                        <RegionOfInterest
                          key={`roi_annotation_id_${annotation.created_at}_${
                            annotation.id || 0
                          }`}
                          annotation={roiAnnotation}
                          scaleInfo={scaleInfo}
                          viewerFacade={props.viewerFacade}
                          isVisible={
                            maskLesionLineType === roiAnnotation.lesionLineType
                          }
                          isSelected={annotation === selectedAnnotation}
                        />
                      )
                    );
                  default:
                    return undefined;
                }
              })}
          {aiOverlay &&
            lesionLine?.wsi_lvl_roi_positions &&
            roiToDziPositions(lesionLine.wsi_lvl_roi_positions).map(
              (p: ROIPosition, i) => (
                <AIRegion
                  key={i}
                  roi={p}
                  scaleInfo={scaleInfo}
                  pixelsPerMeter={props.viewerFacade.getPixelsPerMeter() || 0}
                  type={lesionLine?.diagnosis_name || ""}
                />
              )
            )}
          {aiOverlay &&
            !!lesionLine?.subtypes?.length &&
            (lesionLine?.diagnosis_name !== "Cyst" ||
              features?.cystSubtypeROI) &&
            lesionLine.subtypes.map((subtype) => {
              return (
                subtype.roi_positions &&
                roiToDziPositions(subtype.roi_positions).map((p, i) => (
                  <AIRegion
                    key={i}
                    roi={p}
                    scaleInfo={scaleInfo}
                    pixelsPerMeter={props.viewerFacade.getPixelsPerMeter() || 0}
                    color={
                      subtype.diagnosis_result === "POSITIVE"
                        ? "var(--green)"
                        : "var(--lightGrey)"
                    }
                    type={lesionLine?.diagnosis_name || ""}
                    subtype={subtype}
                  />
                ))
              );
            })}
          {viewerMode === ViewerMode.MEASUREMENT && (
            <MeasurementTool
              scaleInfo={scaleInfo}
              viewerFacade={props.viewerFacade}
              user={auth.user}
              onAnnotationComplete={onAnnotationComplete}
            />
          )}
          {viewerMode === ViewerMode.PEN_DRAWING && (
            <PenDrawingTool
              scaleInfo={scaleInfo}
              viewerFacade={props.viewerFacade}
              user={auth.user}
              newPenDrawing={slideDetail.newPenDrawing}
              startNewPenDrawing={(
                penDrawingAnnotation: PenDrawingAnnotation
              ) => {
                dispatch(startNewPenDrawing(penDrawingAnnotation));
              }}
              onAnnotationComplete={onAnnotationComplete}
            />
          )}
          {viewerMode === ViewerMode.TEXT && (
            <TextTool
              scaleInfo={scaleInfo}
              viewerFacade={props.viewerFacade}
              user={auth.user}
              onAnnotationComplete={onAnnotationComplete}
            />
          )}
          {viewerMode === ViewerMode.AREA_COUNT && (
            <AreaCountTool
              scaleInfo={scaleInfo}
              viewerFacade={props.viewerFacade}
              user={auth.user}
              onAnnotationComplete={onAnnotationComplete}
            />
          )}
          {viewerMode === ViewerMode.ROI && maskLesionLineType && (
            <RegionOfInterestTool
              lesionLineType={maskLesionLineType}
              scaleInfo={scaleInfo}
              viewerFacade={props.viewerFacade}
              user={auth.user}
              onAnnotationComplete={onAnnotationComplete}
            />
          )}
          <rect
            x={0}
            y={0}
            width={scaleInfo.width}
            height={scaleInfo.height}
            style={{
              stroke: "black",
              strokeWidth: 2.0 / scaleInfo.scaleX,
              fillOpacity: 0.0,
              strokeOpacity: 0.5,
            }}
            className="passthrough"
          />
        </svg>
      )}
    </div>
  );
};

export default SvgRoot;
